import React from 'react';
import { clsx } from 'clsx';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'square' | 'squircle';
  status?: 'online' | 'offline' | 'away' | 'busy';
  statusPosition?: 'top' | 'bottom';
  placeholder?: React.ReactNode;
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  shape = 'circle',
  status,
  statusPosition = 'bottom',
  placeholder,
  className,
}) => {
  const sizeClasses = {
    xs: 'w-8',
    sm: 'w-10',
    md: 'w-12',
    lg: 'w-16',
    xl: 'w-24',
  };

  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded',
    squircle: 'mask mask-squircle',
  };

  const statusClasses = {
    online: 'bg-success',
    offline: 'bg-base-300',
    away: 'bg-warning',
    busy: 'bg-error',
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={clsx('avatar', status && status, className)}>
      <div className={clsx(sizeClasses[size], shapeClasses[shape], 'relative')}>
        {src ? (
          <img src={src} alt={alt || name} />
        ) : placeholder ? (
          <div className="bg-base-300 text-base-content w-full h-full flex items-center justify-center">
            {placeholder}
          </div>
        ) : name ? (
          <div className="bg-primary text-primary-content w-full h-full flex items-center justify-center font-semibold">
            {getInitials(name)}
          </div>
        ) : (
          <div className="bg-base-300 w-full h-full" />
        )}

        {status && (
          <span
            className={clsx(
              'absolute w-3 h-3 rounded-full',
              statusClasses[status],
              statusPosition === 'top' ? 'top-0 right-0' : 'bottom-0 right-0',
              'ring-2 ring-base-100'
            )}
          />
        )}
      </div>
    </div>
  );
};

export interface AvatarGroupProps {
  children: React.ReactNode;
  max?: number;
  size?: AvatarProps['size'];
  className?: string;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  max = 3,
  size = 'md',
  className,
}) => {
  const childArray = React.Children.toArray(children);
  const visibleChildren = childArray.slice(0, max);
  const remainingCount = childArray.length - max;

  return (
    <div className={clsx('avatar-group -space-x-6', className)}>
      {visibleChildren}
      {remainingCount > 0 && <Avatar size={size} placeholder={`+${remainingCount}`} />}
    </div>
  );
};
